/**
 * NOVA-POSHTA-117A-R5 — browser-independent replace-on-first-input for selected combobox values.
 * Does not rely on input.select() / setSelectionRange() (often ignored in Telegram mobile WebView).
 */

/** Settlement city search — slightly slower to avoid NP 429. */
export const NP_CITY_DEBOUNCE_MS = 350;
/** Warehouse search — owner R4.5: 150–250 ms. */
export const NP_COMBOBOX_DEBOUNCE_MS = 200;
export const NP_CITY_MIN_QUERY = 2;

/** Strip № / # / "відділення" so numeric re-search matches the NP WarehouseId path. */
export function extractWarehouseNumberQuery(query: string): string | null {
  const t = String(query ?? "").trim();
  if (!t) return null;
  const m =
    t.match(/^\s*[№#]?\s*(\d{1,6})\s*$/u) ||
    t.match(/^(?:відділення|отделение|поштомат|почтомат)\s*[№#]?\s*(\d{1,6})\b/iu) ||
    t.match(/[№#]\s*(\d{1,6})\b/u);
  if (!m?.[1]) return null;
  return String(Number(m[1]));
}

export function warehouseApiQuery(query: string): string {
  const raw = String(query ?? "").trim();
  const num = extractWarehouseNumberQuery(raw);
  if (num) return num;
  return raw.replace(/^[№#]\s*/u, "").slice(0, 80);
}

/** Warehouse: empty query does not fetch; digit / № starts at 1 char; address at 2. */
export function shouldSearchWarehouses(query: string): boolean {
  const q = query.trim();
  if (!q) return false;
  if (extractWarehouseNumberQuery(q)) return true;
  if (/^\d+$/.test(q)) return q.length >= 1;
  return q.length >= 2;
}

export function shouldSearchSettlements(query: string): boolean {
  return query.trim().length >= NP_CITY_MIN_QUERY;
}

export function normalizeDirectoryQuery(raw: string): string {
  return raw
    .normalize("NFC")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase("uk-UA");
}

/**
 * When a selected entity is shown in the field and the user starts editing,
 * replace the entire label with the first real input payload.
 */
export function applyReplaceOnFirstInput(args: {
  replaceArmed: boolean;
  selectedLabel: string | null;
  currentQuery: string;
  nextValue: string;
  insertedData?: string | null;
}): { consumed: boolean; nextQuery: string; shouldClearSelection: boolean } {
  const { replaceArmed, selectedLabel, currentQuery, nextValue, insertedData } = args;
  if (!replaceArmed || !selectedLabel) {
    return { consumed: false, nextQuery: nextValue, shouldClearSelection: nextValue !== (selectedLabel ?? "") };
  }
  if (currentQuery !== selectedLabel) {
    return {
      consumed: false,
      nextQuery: nextValue,
      shouldClearSelection: nextValue !== selectedLabel,
    };
  }

  // Prefer explicit insertion (beforeinput data / single key) over browser append.
  const insert =
    typeof insertedData === "string" && insertedData.length > 0
      ? insertedData
      : nextValue.startsWith(selectedLabel)
        ? nextValue.slice(selectedLabel.length)
        : nextValue.endsWith(selectedLabel) && nextValue.length > selectedLabel.length
          ? nextValue.slice(0, nextValue.length - selectedLabel.length)
          : nextValue !== selectedLabel
            ? nextValue
            : "";

  if (!insert) {
    // Deletion / clear while still showing selected label
    if (nextValue.length < selectedLabel.length || nextValue === "") {
      return { consumed: true, nextQuery: nextValue, shouldClearSelection: true };
    }
    return { consumed: false, nextQuery: nextValue, shouldClearSelection: false };
  }

  return {
    consumed: true,
    nextQuery: insert.slice(0, 120),
    shouldClearSelection: true,
  };
}

export function handleFirstKeyReplaceSelection(
  key: string,
  selectedLabel: string | null,
  query: string,
  opts?: { ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean },
): { handled: boolean; nextQuery: string } {
  if (!selectedLabel || query !== selectedLabel) return { handled: false, nextQuery: query };
  if (opts?.ctrlKey || opts?.metaKey || opts?.altKey) return { handled: false, nextQuery: query };
  if (key.length !== 1) return { handled: false, nextQuery: query };
  if (key === "Enter" || key === "Escape" || key === "Tab") return { handled: false, nextQuery: query };
  return { handled: true, nextQuery: key };
}

/** Customer-facing copy — never expose method names / refs / task ids / HTTP 429. */
export function npDirectoryErrorCopy(lang: "uk" | "en"): string {
  return lang === "en"
    ? "Could not load branches"
    : "Не вдалося завантажити відділення";
}

export function npRateLimitTitle(lang: "uk" | "en"): string {
  return lang === "en"
    ? "Nova Poshta temporarily limited requests"
    : "Нова Пошта тимчасово обмежила кількість запитів";
}

export function npRateLimitBody(lang: "uk" | "en"): string {
  return lang === "en"
    ? "Wait a few seconds. Your city and branch selection is kept."
    : "Зачекайте кілька секунд. Ваш вибір міста та відділення збережено.";
}

export function npRateLimitRetryLabel(lang: "uk" | "en"): string {
  return lang === "en" ? "Try again" : "Спробувати ще раз";
}

export function npRateLimitChangeCityLabel(lang: "uk" | "en"): string {
  return lang === "en" ? "Change city" : "Змінити місто";
}

export function npCityHintCopy(lang: "uk" | "en", kind: "min" | "loading" | "empty"): string {
  if (lang === "en") {
    if (kind === "min") return "Enter at least 2 characters";
    if (kind === "loading") return "Searching…";
    return "Settlement not found";
  }
  if (kind === "min") return "Введіть щонайменше 2 символи";
  if (kind === "loading") return "Шукаємо…";
  return "Населений пункт не знайдено";
}

export function npWarehouseLoadingCopy(lang: "uk" | "en"): string {
  return lang === "en" ? "Searching branches…" : "Шукаємо відділення…";
}

export function npWarehouseEmptyCopy(
  lang: "uk" | "en",
  _mode: "branch" | "parcel_locker",
): string {
  void _mode;
  if (lang === "en") {
    return "No branches found. Try the number or part of the address.";
  }
  return "Не знайшли відділення. Спробуйте номер або частину адреси.";
}

export type DeliveryAnalyticsEvent =
  | "delivery.city_search_started"
  | "delivery.city_selected"
  | "delivery.type_changed"
  | "delivery.warehouse_search_started"
  | "delivery.warehouse_selected"
  | "delivery.directory_error";

/** Official NP Ref shape (UUID-like) — labels/free text never qualify. */
export function looksLikeOfficialNovaPoshtaRef(ref: string | null | undefined): boolean {
  if (!ref || ref.length < 8) return false;
  return /^[0-9a-f-]{8,}$/i.test(ref);
}

/** Reject free-text / mismatched pairs before placeOrder (client-side). */
export function isCheckoutDeliverySelectionConsistent(sel: {
  settlementRef?: string | null;
  warehouseRef?: string | null;
  mode?: string | null;
  building?: string | null;
} | null): boolean {
  if (!sel?.settlementRef || !looksLikeOfficialNovaPoshtaRef(sel.settlementRef)) return false;
  if (sel.mode === "address") return Boolean(sel.building?.trim());
  return looksLikeOfficialNovaPoshtaRef(sel.warehouseRef);
}

/** Pseudonymized only — never attach query text / PII. */
export function emitDeliveryAnalytics(
  event: DeliveryAnalyticsEvent,
  meta?: { deliveryType?: string; hasResults?: boolean },
): void {
  try {
    const detail: Record<string, unknown> = { event, t: Date.now() };
    if (meta?.deliveryType) detail.deliveryType = meta.deliveryType;
    if (typeof meta?.hasResults === "boolean") detail.hasResults = meta.hasResults;
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("asiastyle:delivery", { detail }));
    }
  } catch {
    /* ignore */
  }
}
